import { useEffect, useState } from "react";
import {
  deletePrivacyPolicy,
  getPrivacyPolicyId,
} from "../../api/privacyPolicy";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import CommonHeader from "../../CommonComponent/CommonHeader";
import CustomBreadcrumb from "../../CommonComponent/CommonBreadcrumb";
import Loader from "../../CommonComponent/Loader";
import CommonTabs from "../../CommonComponent/CommonTabs";
import ButtonCommon from "../../CommonComponent/ButtonCommon";

const PrivacyPolicyDetails = () => {
  const [htmlContent, setHtmlContent] = useState("");
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPolicy = async () => {
      if (id) {
        setLoading(true);
        try {
          const res = await getPrivacyPolicyId(id);
          setPolicyData(res?.data);
          setHtmlContent(res?.data?.html_content);
        } catch (err) {
          showErrorToast("Failed to fetch policy");
        } finally {
          setLoading(false);
        }
      }
    };
    fetchPolicy();
  }, [id]);

  const breadcrumbItems = [
    {
      text: "Privacy Policy",
      href: "/privacy-policy",
    },
    {
      text: "Privacy Policy Details",
    },
  ];

  const tabs = [
    {
      label: "Privacy Policy",
      value: "1",
      content: (
        <iframe
          title="Privacy Policy Preview"
          srcDoc={htmlContent}
          style={{ width: "100%", height: "100vh", border: "none" }}
        />
      ),
    },
  ];

  // const renderKeyValue = (data, level = 0) => {
  //   if (!data || typeof data !== "object") return null;

  //   return Object.entries(data).map(([key, value]) => {
  //     const formattedKey = key
  //       .replace(/([A-Z])/g, " $1")
  //       .replace(/_/g, " ")
  //       .toLowerCase();

  //     return (
  //       <Box key={key} sx={{ pl: level * 2, mb: 1 }}>
  //         <Typography
  //           variant="caption"
  //           sx={{ color: "text.secondary", fontWeight: 500 }}
  //         >
  //           {formattedKey}
  //         </Typography>

  //         {Array.isArray(value) ? (
  //           value.length > 0 ? (
  //             <Box sx={{ pl: 2, pt: 0.5 }}>
  //               {value.map((item, idx) => (
  //                 <Typography key={idx} variant="body2">
  //                   • {item}
  //                 </Typography>
  //               ))}
  //             </Box>
  //           ) : (
  //             <Typography variant="body2" sx={{ pl: 2 }}>
  //               No items
  //             </Typography>
  //           )
  //         ) : typeof value === "object" && value !== null ? (
  //           <Box sx={{ mt: 1 }}>{renderKeyValue(value, level + 1)}</Box>
  //         ) : (
  //           <Typography variant="body2" sx={{ pl: 1 }}>
  //             {value === null || value === "" ? "N/A" : String(value)}
  //           </Typography>
  //         )}
  //       </Box>
  //     );
  //   });
  // };

  const handleEdit = (privacyId) => {
    navigate("/privacy-settings", {
      state: {
        privacyId,
        isEditing: true,
      },
    });
  };

  const handleDelete = async (privacyId) => {
    setLoading(true);
    try {
      const result = await deletePrivacyPolicy(privacyId);
      if (result?.status_code === 200) {
        navigate("/privacy-policy");
        showSuccessToast("Deleted Successfully");
        setLoading(false);
      }
    } catch (error) {
      showErrorToast(error);
      setLoading(false);
      console.error("Failed to delete policy", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadHtml = (htmlContent, policyName) => {
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    // Sanitize filename (optional but recommended)
    const sanitizedFileName = policyName
      ? policyName.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9-_]/g, "")
      : "privacy_policy";

    a.href = url;
    a.download = `${sanitizedFileName}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 3 }}>
        <CommonHeader
          title="Privacy Policy Details"
          subTitle="You can review and audit the privacy policy details on this page."
        />
        <Box display="flex" justifyContent="space-between" gap={2}>
          <ButtonCommon
            onClick={() => handleEdit(policyData.policy_id)}
            disabled={loading}
          >
            Edit
          </ButtonCommon>
          <ButtonCommon
            onClick={() => handleDelete(policyData.policy_id)}
            disabled={loading}
          >
            Delete
          </ButtonCommon>
          {/* {policyData?.hosting_preference !== "dpc" && ( */}
          <ButtonCommon
            disabled={loading}
            onClick={() =>
              handleDownloadHtml(
                policyData.html_content,
                policyData.policy_name
              )
            }
          >
            Download
          </ButtonCommon>
          {/* )} */}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <CustomBreadcrumb
          items={breadcrumbItems}
          onBackClick={() => navigate("/privacy-policy")}
        />
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, backgroundColor: "#F9FAFB", height: "100%" }}>
              <Typography
                variant="h6"
                gutterBottom
                color="#525455"
                fontWeight="bold"
              >
                Privacy Policy Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {policyData &&
                Object.entries(policyData)
                  .filter(([key]) => key !== "html_content" && key !== "config")
                  .map(([key, value], index, array) => {
                    const formattedKey = key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/_/g, " ")
                      .toLowerCase();

                    return (
                      <Box key={key} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ textTransform: "capitalize", fontWeight: 600 }}
                        >
                          {formattedKey}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                        >
                          {String(value)}
                        </Typography>

                        {!(index + 1 === array.length) && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Box>
                    );
                  })}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <CommonTabs value={"1"} tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PrivacyPolicyDetails;
